import { useState, useEffect } from 'react'
import { useLocation } from 'react-router-dom'
import { ReactiveJsonRoot } from '@ea-lab/reactive-json'
import { routingConfig, isPageExcluded } from '../../config/routes'
import { buildDataOverride } from './urlDataOverride'
import NotFoundPage from './NotFoundPage'

const DynamicPageRouter = ({ plugins }) => {
  const location = useLocation()
  const [pageExists, setPageExists] = useState(true)
  const [pageContent, setPageContent] = useState(null)
  const [dataOverride, setDataOverride] = useState(undefined)
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState(null)

  const getResourceInfoFromPath = (pathname) => {
    if (pathname === '/') {
      return { category: 'page', name: routingConfig.defaultPage, id: null }
    }

    const cleanedPath = pathname.startsWith('/') ? pathname.slice(1) : pathname
    const segments = cleanedPath.split('/')

    const lastSegment = segments[segments.length - 1]
    const isLastSegmentId = /^\d+$/.test(lastSegment)

    let pathSegments = [...segments]
    let pathId = null

    if (isLastSegmentId) {
      pathId = lastSegment
      pathSegments.pop()
    }

    const firstSegment = pathSegments[0]

    if (firstSegment === 'composant' || firstSegment === 'components' || firstSegment === 'component') {
      const restSegments = pathSegments.slice(1)
      const name = (restSegments.join('/') || '').replace(/\/$/, '')
      return { category: 'component', name, id: pathId }
    }

    const name = pathSegments.join('/').replace(/\/$/, '')
    return { category: 'page', name, id: pathId }
  }

  const { category, name: resourceName, id: pathId } = getResourceInfoFromPath(location.pathname)

  const yamlPath =
    category === 'component'
      ? `${routingConfig.componentsDirectory}${resourceName}${routingConfig.pageExtension}`
      : `${routingConfig.pagesDirectory}${resourceName}${routingConfig.pageExtension}`

  useEffect(() => {
    const checkPageAndLoadContent = async () => {
      setIsLoading(true)
      setError(null)

      if (category === 'page' && isPageExcluded(resourceName)) {
        setPageExists(false)
        setError('Cette page est gérée par une route spécifique.')
        setIsLoading(false)
        return
      }

      try {
        const response = await fetch(yamlPath)

        if (!response.ok) {
          setPageExists(false)
          setIsLoading(false)
          return
        }

        const content = await response.text()
        const trimmed = content.trim()

        if (
          trimmed.startsWith('<!') ||
          trimmed.startsWith('<html') ||
          (!trimmed.includes(':') && !trimmed.startsWith('{'))
        ) {
          setPageExists(false)
          setError('Le fichier retourné n\'est pas un document YAML/JSON valide.')
          setIsLoading(false)
          return
        }

        setPageContent(content)
        setDataOverride(buildDataOverride(content, location.search, pathId))
        setPageExists(true)
      } catch {
        setPageExists(false)
        setError('Erreur de réseau lors du chargement de la page.')
      } finally {
        setIsLoading(false)
      }
    }

    checkPageAndLoadContent()
  }, [yamlPath, resourceName, category, location.search])

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-10 w-10 border-2 border-stone-200 border-t-stone-600 mx-auto mb-4" />
          <p className="text-sm text-stone-500">Chargement…</p>
        </div>
      </div>
    )
  }

  if (!pageExists || !pageContent) {
    return (
      <NotFoundPage
        title={category === 'component' ? 'Composant introuvable' : undefined}
        message={
          category === 'component'
            ? `Le composant "${resourceName}" n'existe pas.`
            : undefined
        }
        technical={error || undefined}
      />
    )
  }

  return (
    <ReactiveJsonRoot
      key={`${yamlPath}-${location.search}`}
      maybeRawAppRjBuild={pageContent}
      dataOverride={dataOverride}
      rjBuildFetchMethod="GET"
      plugins={plugins}
    />
  )
}

export default DynamicPageRouter
